import { useMemo, useCallback } from "react";
import { ActivityIndicator, View } from "@-/com.npmjs.react-native";
import { useQueries, useQuery } from "@-/com.npmjs.tanstack__react-query";
import {
  useGetMorphemeDetailsTileSliceQueryKey,
  morphemeDetailsTileSliceQueryFn,
} from "@-/dev.improve.haniverse.lib.morpheme.read";
import { useMorphemeListStagesQuery } from "@-/dev.improve.haniverse.lib.morpheme.stage";
import { MorphemeDetailsTileLink } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.details";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";

/** Resolve only the four mounted detail tiles; the shared loader batches their reads. */
export function WordDetailsTilesRow({
  onNavigate,
  size,
  start,
  words,
}: {
  onNavigate: (word: string) => void;
  size: number;
  start: number;
  words: readonly string[];
}) {
  const rowWords = useMemo(() => words.slice(start, start + 4), [start, words]);
  const getQueryKey = useGetMorphemeDetailsTileSliceQueryKey();
  const results = useQueries({
    queries: rowWords.map((morpheme) => ({
      queryFn: morphemeDetailsTileSliceQueryFn,
      queryKey: getQueryKey({ morpheme }),
    })),
  });
  const stages = useQuery(useMorphemeListStagesQuery(rowWords));
  const handleNavigate = useCallback(
    (card: MorphemeDetailsTileSlice) => onNavigate(card.front),
    [onNavigate],
  );
  if (stages.error !== null) {
    throw stages.error;
  }
  return (
    <View className="flex-row gap-1">
      {rowWords.map((word, index) => {
        const result = results[index];
        if (result?.error != null) {
          throw result.error;
        }
        const card = result?.data;
        return (
          <View className="overflow-hidden" key={word} style={{ height: size, width: size }}>
            {card === undefined ? (
              <View className="flex-1 items-center justify-center">
                <ActivityIndicator accessibilityLabel={`Loading ${word} details`} />
              </View>
            ) : card === null ? (
              <View className="flex-1 items-center justify-center">
                <Text>{word}</Text>
                <Text colorVariant="muted">No details</Text>
              </View>
            ) : (
              <MorphemeDetailsTileLink
                graphemeCard={card}
                onNavigate={handleNavigate}
                size={size}
                stage={stages.data?.[word] ?? "unadded"}
              />
            )}
          </View>
        );
      })}
    </View>
  );
}
